var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataGrid, { SelectColumn } from 'react-data-grid';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useToast } from '../Toast';
import Row from '../Dategrid/DGRow';
const defaultNormalizeRows = (rows) => (rows || []).map((row, idx) => {
    var _a, _b;
    return (Object.assign(Object.assign({}, row), { id: (_b = (_a = row === null || row === void 0 ? void 0 : row.id) !== null && _a !== void 0 ? _a : row === null || row === void 0 ? void 0 : row.key) !== null && _b !== void 0 ? _b : idx + 1 }));
});
const formatIndianForDisplay = (value, decimals = 2) => {
    if (value === '' || value === null || value === undefined)
        return '';
    const num = Number(String(value).replace(/,/g, ''));
    if (isNaN(num))
        return value;
    return num.toLocaleString('en-IN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDdMmmYyyy = (date) => {
    var _a;
    const dd = String(date.getDate()).padStart(2, '0');
    const mmm = (_a = MONTHS_SHORT[date.getMonth()]) !== null && _a !== void 0 ? _a : '';
    const yyyy = String(date.getFullYear());
    return `${dd}-${mmm}-${yyyy}`;
};
const parseToDate = (value) => {
    if (!value)
        return null;
    if (value instanceof Date && !isNaN(value.getTime()))
        return value;
    const s = String(value).trim();
    if (!s)
        return null;
    // yyyy-mm-dd
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [y, m, d] = s.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        return isNaN(dt.getTime()) ? null : dt;
    }
    // dd-MMM-yyyy
    const m = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(s);
    if (m) {
        const day = Number(m[1]);
        const mon = m[2].toLowerCase();
        const year = Number(m[3]);
        const monthIndex = MONTHS_SHORT.findIndex((mm) => mm.toLowerCase() === mon);
        if (monthIndex >= 0) {
            const dt = new Date(year, monthIndex, day);
            return isNaN(dt.getTime()) ? null : dt;
        }
    }
    const parsed = new Date(s);
    return isNaN(parsed.getTime()) ? null : parsed;
};
const parseTimeToDate = (timeStr) => {
    if (!timeStr)
        return null;
    if (timeStr instanceof Date && !isNaN(timeStr.getTime()))
        return timeStr;
    const s = String(timeStr).trim();
    if (!s)
        return null;
    // "hh:mm AM/PM"
    if (s.includes(' ') && (s.toUpperCase().includes('AM') || s.toUpperCase().includes('PM'))) {
        const parts = s.split(' ');
        if (parts.length !== 2)
            return null;
        const [time, periodRaw] = parts;
        const period = periodRaw.toUpperCase();
        const [hhRaw, mmRaw] = time.split(':');
        const hh = Number(hhRaw);
        const mm = Number(mmRaw);
        if (!Number.isFinite(hh) || !Number.isFinite(mm))
            return null;
        let hours24 = hh;
        if (period === 'PM' && hh !== 12)
            hours24 = hh + 12;
        if (period === 'AM' && hh === 12)
            hours24 = 0;
        const dt = new Date();
        dt.setHours(hours24, mm, 0, 0);
        return dt;
    }
    // "hh:mm" 24-hour
    if (/^\d{1,2}:\d{2}$/.test(s)) {
        const [hhRaw, mmRaw] = s.split(':');
        const hh = Number(hhRaw);
        const mm = Number(mmRaw);
        if (!Number.isFinite(hh) || !Number.isFinite(mm))
            return null;
        const dt = new Date();
        dt.setHours(hh, mm, 0, 0);
        return dt;
    }
    const parsed = new Date(s);
    return isNaN(parsed.getTime()) ? null : parsed;
};
const formatHhMmA = (date) => {
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    if (hours === 0)
        hours = 12;
    const hh = String(hours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');
    return `${hh}:${mm} ${ampm}`;
};
const EditableTable = ({ initialRows, columns, enableRowSelection = false, onChange, canAddRow, allowAddRow = true, allowDeleteRow = true, onTableChange = () => { }, newRowDefaults, showFooter = false, footerTotalColumnKey = 'netamt', showAddRowButton = true, showRemoveSelectedButton = true, showRowDelete = true, deleteColumnPosition = 'end', deleteButtonVariant = 'icon', disableAddWhenInvalid = false, showMissingRequiredToast = true, stretchLastColumn = false, showInlineAddRow = false, inlineAddRowLabel = 'Add', className = 'rdg-light item-table', gridStyle, normalizeRows }) => {
    const { showError } = useToast();
    const [rows, setRows] = useState([]);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const gridWrapRef = useRef(null);
    const gridRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const pendingSelectAfterAddRef = useRef(null);
    useEffect(() => {
        const el = gridWrapRef.current;
        if (!el)
            return;
        const update = () => {
            const rect = el.getBoundingClientRect();
            setContainerWidth(Math.floor(rect.width));
        };
        update();
        const ro = new ResizeObserver(() => update());
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const inlineAddRowItem = useMemo(() => ({ __inlineAddRow: true, id: '__inline_add_row__' }), []);
    const isInlineAddRow = (row) => !!(row === null || row === void 0 ? void 0 : row.__inlineAddRow);
    const DropdownEditor = ({ row, colKey, options, onRowChange, onClose }) => {
        const dropdownRef = useRef(null);
        const panelClassName = useMemo(() => `rdg-dropdown-panel-${Math.random().toString(36).slice(2)}`, []);
        const [isOpen, setIsOpen] = useState(false);
        const [highlightedIndex, setHighlightedIndex] = useState(0);
        const highlightedIndexRef = useRef(0);
        const [filterValue, setFilterValue] = useState('');
        const visibleOptions = useMemo(() => {
            const all = options !== null && options !== void 0 ? options : [];
            const q = filterValue.trim().toLowerCase();
            if (!q)
                return all;
            return all.filter((opt) => { var _a; return String((_a = opt === null || opt === void 0 ? void 0 : opt.label) !== null && _a !== void 0 ? _a : '').toLowerCase().includes(q); });
        }, [options, filterValue]);
        const visibleOptionsRef = useRef(visibleOptions);
        const rowRef = useRef(row);
        const currentValueRef = useRef(row[colKey]);
        useEffect(() => {
            highlightedIndexRef.current = highlightedIndex;
        }, [highlightedIndex]);
        useEffect(() => {
            visibleOptionsRef.current = visibleOptions;
        }, [visibleOptions]);
        useEffect(() => {
            rowRef.current = row;
            currentValueRef.current = row[colKey];
        }, [row]);
        const commitDropdownValue = useCallback((rawValue) => {
            const value = isNaN(Number(rawValue)) ? rawValue : Number(rawValue);
            const selectedOption = options === null || options === void 0 ? void 0 : options.find((opt) => opt.value === value);
            if (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.onSelect) {
                selectedOption.onSelect({
                    row: rowRef.current,
                    colKey,
                    value,
                    option: selectedOption,
                    onRowChange,
                    onClose
                });
                return;
            }
            const updatedRow = Object.assign(Object.assign({}, rowRef.current), { [colKey]: value });
            if (colKey === 'item_key' && (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.gst) !== undefined) {
                updatedRow.gst = selectedOption.gst;
            }
            onRowChange(updatedRow, true);
        }, [colKey, onRowChange, options]);
        const applyHighlight = useCallback((idx) => {
            const panel = document.querySelector(`.${panelClassName}`);
            if (!panel)
                return;
            const items = Array.from(panel.querySelectorAll('li.p-dropdown-item'));
            if (!items.length)
                return;
            items.forEach((el) => {
                el.classList.remove('p-highlight');
                el.setAttribute('aria-selected', 'false');
            });
            const clamped = Math.max(0, Math.min(idx, items.length - 1));
            const active = items[clamped];
            if (!active)
                return;
            active.classList.add('p-highlight');
            active.setAttribute('aria-selected', 'true');
            active.scrollIntoView({ block: 'nearest' });
        }, [panelClassName]);
        useEffect(() => {
            if (!isOpen)
                return;
            // Default highlight when panel opens (or filter changes).
            setHighlightedIndex(0);
            requestAnimationFrame(() => applyHighlight(0));
        }, [applyHighlight, filterValue, isOpen]);
        useEffect(() => {
            if (!isOpen)
                return;
            requestAnimationFrame(() => applyHighlight(highlightedIndex));
        }, [applyHighlight, highlightedIndex, isOpen]);
        useEffect(() => {
            if (!isOpen)
                return;
            const onKeyDown = (ev) => {
                var _a, _b, _c, _d, _e, _f, _g, _h;
                const target = ev.target;
                if (!target)
                    return;
                const inPanel = !!target.closest(`.${panelClassName}`);
                const dropdownEl = ((_c = (_b = (_a = dropdownRef.current) === null || _a === void 0 ? void 0 : _a.getElement) === null || _b === void 0 ? void 0 : _b.call(_a)) !== null && _c !== void 0 ? _c : (_d = dropdownRef.current) === null || _d === void 0 ? void 0 : _d.container);
                const inDropdown = !!dropdownEl && dropdownEl.contains(target);
                if (!inPanel && !inDropdown)
                    return;
                if (ev.key !== 'ArrowDown' && ev.key !== 'ArrowUp' && ev.key !== 'Enter')
                    return;
                ev.preventDefault();
                ev.stopPropagation();
                // Prevent PrimeReact/React handlers from seeing this key event.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (_f = (_e = ev).stopImmediatePropagation) === null || _f === void 0 ? void 0 : _f.call(_e);
                const opts = visibleOptionsRef.current;
                if (ev.key === 'Enter') {
                    const opt = opts[highlightedIndexRef.current];
                    if (opt) {
                        commitDropdownValue(opt.value);
                        (_h = (_g = dropdownRef.current) === null || _g === void 0 ? void 0 : _g.hide) === null || _h === void 0 ? void 0 : _h.call(_g);
                    }
                    return;
                }
                if (!opts.length)
                    return;
                const delta = ev.key === 'ArrowDown' ? 1 : -1;
                setHighlightedIndex((prev) => {
                    const next = Math.max(0, Math.min(prev + delta, opts.length - 1));
                    highlightedIndexRef.current = next;
                    return next;
                });
            };
            document.addEventListener('keydown', onKeyDown, true);
            return () => document.removeEventListener('keydown', onKeyDown, true);
        }, [commitDropdownValue, isOpen, panelClassName]);
        useEffect(() => {
            const timer = setTimeout(() => {
                if (dropdownRef.current)
                    dropdownRef.current.show();
            }, 50);
            return () => clearTimeout(timer);
        }, []);
        return (React.createElement("div", { className: "rdg-editor-container" },
            React.createElement(Dropdown, { ref: dropdownRef, autoFocus: true, style: { width: '100%' }, className: "p-inputtext-sm rdg-select", value: row[colKey], optionLabel: "label", optionValue: "value", filter: true, filterBy: "label", panelClassName: panelClassName, options: options, onChange: (e) => {
                    currentValueRef.current = e.value;
                    commitDropdownValue(e.value);
                }, onFilter: (e) => { var _a; return setFilterValue((_a = e.filter) !== null && _a !== void 0 ? _a : ''); }, onShow: () => setIsOpen(true), onHide: () => setIsOpen(false), onKeyDown: (e) => {
                    if (e.key === 'Enter') {
                        e.stopPropagation();
                    }
                    else if (e.key === 'Tab') {
                        e.stopPropagation();
                        commitDropdownValue(currentValueRef.current);
                        onClose(true);
                    }
                } })));
    };
    const TextEditor = ({ row, colKey, onRowChange, onClose }) => {
        return (React.createElement("div", { className: "rdg-editor-container" },
            React.createElement(InputText, { autoFocus: true, value: row[colKey] || '', onChange: (e) => onRowChange(Object.assign(Object.assign({}, row), { [colKey]: e.target.value }), true), onBlur: (e) => {
                    onRowChange(Object.assign(Object.assign({}, row), { [colKey]: e.target.value }), true);
                    onClose(true);
                }, onKeyDown: (e) => {
                    if (e.key === 'Enter') {
                        onRowChange(Object.assign(Object.assign({}, row), { [colKey]: e.target.value }), true);
                        onClose(true);
                        e.preventDefault();
                        e.stopPropagation();
                    }
                    else if (e.key === 'Escape') {
                        onClose(true);
                        e.preventDefault();
                        e.stopPropagation();
                    }
                    else if (e.key === 'Tab') {
                        e.stopPropagation();
                        onRowChange(Object.assign(Object.assign({}, row), { [colKey]: e.target.value }), true);
                        onClose(true);
                    }
                    else {
                        e.stopPropagation();
                    }
                }, style: { width: '100%' }, className: "p-inputtext-sm" })));
    };
    const CalendarEditor = ({ row, colKey, onRowChange, onClose, mode }) => {
        const value = mode === 'time' ? parseTimeToDate(row[colKey]) : parseToDate(row[colKey]);
        const [currentTime, setCurrentTime] = useState(mode === 'time' ? value : null);
        useEffect(() => {
            if (mode !== 'time')
                return;
            setCurrentTime(value);
        }, [mode, value]);
        const timeValue = mode === 'time' ? currentTime !== null && currentTime !== void 0 ? currentTime : undefined : undefined;
        const dateValue = mode === 'date' ? value !== null && value !== void 0 ? value : undefined : undefined;
        return (React.createElement("div", { className: "rdg-editor-container" },
            React.createElement(Calendar, { style: { width: '100%' }, className: "p-inputtext-sm", value: mode === 'time' ? timeValue : dateValue, showIcon: true, dateFormat: mode === 'date' ? 'dd-M-yy' : undefined, monthNavigator: mode === 'date', yearNavigator: mode === 'date', yearRange: mode === 'date' ? '2000:2100' : undefined, timeOnly: mode === 'time', hourFormat: "12", showTime: mode === 'time', showSeconds: false, stepMinute: 1, stepHour: 1, onChange: (e) => {
                    const dt = e === null || e === void 0 ? void 0 : e.value;
                    if (!dt)
                        return;
                    const next = Object.assign({}, row);
                    if (mode === 'time') {
                        setCurrentTime(dt);
                        next[colKey] = formatHhMmA(dt);
                        // Match previous behavior: update value but don't close/commit until picker hides.
                        onRowChange(next, false);
                    }
                    else {
                        next[colKey] = formatDdMmmYyyy(dt);
                        onRowChange(next, true);
                    }
                }, onHide: () => {
                    onClose(true);
                }, tabIndex: -1 })));
    };
    const activateCell = (cell, openEditor = true) => {
        if (!cell)
            return;
        cell.click();
        if (!openEditor)
            return;
        // Give the grid time to update selection/focus before attempting to enter edit mode.
        requestAnimationFrame(() => {
            cell.dispatchEvent(new MouseEvent('dblclick', { bubbles: true }));
            requestAnimationFrame(() => {
                var _a, _b;
                const editorInput = cell.querySelector('.rdg-editor-container input, .rdg-editor-container textarea, .rdg-editor-container [tabindex]');
                if (editorInput) {
                    editorInput.focus();
                    // Select text for inputs when available (best-effort)
                    try {
                        (_b = (_a = editorInput).select) === null || _b === void 0 ? void 0 : _b.call(_a);
                    }
                    catch (_c) { }
                }
            });
        });
    };
    const focusOpenEditorInput = () => {
        // Best-effort focus: only one editor should be open at a time.
        requestAnimationFrame(() => {
            var _a, _b, _c;
            const scope = (_a = gridWrapRef.current) !== null && _a !== void 0 ? _a : document;
            const editorInput = scope.querySelector('.rdg-editor-container input, .rdg-editor-container textarea, .rdg-editor-container [tabindex]');
            if (!editorInput)
                return;
            editorInput.focus();
            try {
                (_c = (_b = editorInput).select) === null || _c === void 0 ? void 0 : _c.call(_b);
            }
            catch (_d) { }
        });
    };
    const selectFirstEditableCell = (rowIdx) => {
        var _a, _b, _c, _d, _e;
        let firstEditableColIndex = 0;
        while (firstEditableColIndex < columns.length &&
            (((_a = columns[firstEditableColIndex]) === null || _a === void 0 ? void 0 : _a.editor) === 'disabled' || !((_b = columns[firstEditableColIndex]) === null || _b === void 0 ? void 0 : _b.editor))) {
            firstEditableColIndex++;
        }
        const leadingCols = (enableRowSelection ? 1 : 0) + (showRowDelete && allowDeleteRow && deleteColumnPosition === 'start' ? 1 : 0);
        const gridColIndex = firstEditableColIndex + leadingCols;
        // Prefer DataGrid API for reliable focus/edit-mode (avoids landing on the header).
        // Two-phase select helps when focus comes from a button click (inline add row) and RDG is not yet ready to render the editor.
        const position = { rowIdx, idx: gridColIndex };
        (_c = gridRef.current) === null || _c === void 0 ? void 0 : _c.scrollToRow(rowIdx);
        (_d = gridRef.current) === null || _d === void 0 ? void 0 : _d.scrollToColumn(gridColIndex);
        (_e = gridRef.current) === null || _e === void 0 ? void 0 : _e.selectCell(position, false);
        requestAnimationFrame(() => {
            var _a, _b, _c, _d;
            (_c = (_b = (_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.element) === null || _b === void 0 ? void 0 : _b.focus) === null || _c === void 0 ? void 0 : _c.call(_b);
            (_d = gridRef.current) === null || _d === void 0 ? void 0 : _d.selectCell(position, true);
            focusOpenEditorInput();
        });
    };
    const getBodyRows = (gridEl) => {
        const allRows = Array.from(gridEl.querySelectorAll('[role="row"]'));
        // Header rows use columnheader; data rows contain gridcells.
        return allRows.filter((r) => !!r.querySelector('[role="gridcell"]'));
    };
    useEffect(() => {
        const normalized = (normalizeRows !== null && normalizeRows !== void 0 ? normalizeRows : defaultNormalizeRows)(initialRows || []);
        setRows(normalized);
    }, [initialRows, normalizeRows]);
    const handleRowsChange = (updatedRows, { indexes: _indexes }) => {
        const cleaned = showInlineAddRow ? updatedRows.filter((r) => !isInlineAddRow(r)) : updatedRows;
        setRows(cleaned);
        onChange === null || onChange === void 0 ? void 0 : onChange(cleaned);
    };
    const isRowMissingRequired = (row) => {
        if (!row)
            return false;
        return columns.some((col) => {
            if (!col.required)
                return false;
            const value = row === null || row === void 0 ? void 0 : row[col.key];
            if (value === '' || value === null || value === undefined)
                return true;
            if (col.editor === 'number') {
                const num = Number(String(value).replace(/,/g, '').trim());
                if (!Number.isFinite(num) || num === 0)
                    return true;
            }
            return false;
        });
    };
    const isAddDisabled = () => {
        if (!allowAddRow)
            return true;
        if (typeof canAddRow === 'function' && !canAddRow(rows))
            return true;
        if (!disableAddWhenInvalid)
            return false;
        if (rows.length === 0)
            return false;
        return isRowMissingRequired(rows[rows.length - 1]);
    };
    const handleAddRow = () => {
        if (isAddDisabled()) {
            if (showMissingRequiredToast) {
                showError('Missing Required Fields', 'Please fill all required fields before adding a new row.');
            }
            return;
        }
        const lastRow = rows[rows.length - 1];
        const missingRequired = isRowMissingRequired(lastRow);
        if (lastRow && missingRequired) {
            if (showMissingRequiredToast) {
                showError('Missing Required Fields', 'Please fill all required fields before adding a new row.');
            }
            return;
        }
        const numericIds = rows
            .map((r) => Number(r === null || r === void 0 ? void 0 : r.id))
            .filter((n) => Number.isFinite(n));
        const nextId = numericIds.length ? Math.max(...numericIds) + 1 : rows.length + 1;
        const newRow = Object.assign({ id: nextId }, (newRowDefaults || {}));
        columns.forEach((col) => {
            if (col.key !== 'id' && newRow[col.key] === undefined) {
                newRow[col.key] = '';
            }
        });
        const updatedRows = [...rows, newRow];
        setRows(updatedRows);
        onChange === null || onChange === void 0 ? void 0 : onChange(updatedRows);
        onTableChange(true);
        pendingSelectAfterAddRef.current = { rowIdx: updatedRows.length - 1 };
        setTimeout(() => {
            const pending = pendingSelectAfterAddRef.current;
            if (!pending)
                return;
            pendingSelectAfterAddRef.current = null;
            selectFirstEditableCell(pending.rowIdx);
        }, 0);
    };
    const handleDeleteRow = (rowToDelete) => {
        if (isInlineAddRow(rowToDelete))
            return;
        const updatedRows = rows.filter((row) => row.id !== rowToDelete.id);
        setRows(updatedRows);
        setSelectedRows((prev) => {
            const updated = new Set(prev);
            updated.delete(rowToDelete.id);
            return updated;
        });
        onChange === null || onChange === void 0 ? void 0 : onChange(updatedRows);
        onTableChange(true);
    };
    const handleBulkDelete = () => {
        const updatedRows = rows.filter((row) => !selectedRows.has(row.id) && !isInlineAddRow(row));
        setRows(updatedRows);
        setSelectedRows(new Set());
        onChange === null || onChange === void 0 ? void 0 : onChange(updatedRows);
        onTableChange(true);
    };
    const NumberEditor = ({ row, colKey, onRowChange, onClose }) => {
        const decimals = colKey === 'qty' ? 3 : 2;
        const [value, setValue] = useState(row[colKey] === '' || row[colKey] === null || row[colKey] === undefined ? '' : String(row[colKey]));
        const inputRef = useRef(null);
        useEffect(() => {
            const val = row[colKey];
            setValue(val === '' || val === null || val === undefined ? '' : String(val));
        }, [row, colKey]);
        useEffect(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                try {
                    inputRef.current.select();
                }
                catch (_a) { }
            }
        }, []);
        const commitValue = (rawValue) => {
            const cleaned = rawValue.replace(/,/g, '').trim();
            const parsed = cleaned === '' ? '' : parseFloat(cleaned);
            const updatedRow = Object.assign(Object.assign({}, row), { [colKey]: parsed });
            if (colKey === 'netamt' && parsed !== '') {
                const gst = parseFloat(row.gst) || 0;
                updatedRow.gstamt = parseFloat((Number(parsed) * (gst * 0.01)).toFixed(2));
            }
            if (colKey === 'gst' && parsed !== '') {
                const netamt = parseFloat(row.netamt) || 0;
                updatedRow.gstamt = parseFloat((Number(parsed) * (netamt * 0.01)).toFixed(2));
            }
            onRowChange(updatedRow, true);
        };
        return (React.createElement("div", { className: "rdg-editor-container" },
            React.createElement("input", { ref: inputRef, type: "text", value: value, onFocus: (e) => {
                    setValue((v) => v.toString().replace(/,/g, ''));
                    try {
                        e.target.select();
                    }
                    catch (_a) { }
                }, onChange: (e) => {
                    const raw = e.target.value;
                    if (/^-?\d*\.?\d*$/.test(raw) || raw === '') {
                        setValue(raw);
                    }
                }, onBlur: (e) => {
                    const raw = e.target.value;
                    commitValue(raw);
                    const cleaned = raw.replace(/,/g, '').trim();
                    const parsed = cleaned === '' ? '' : parseFloat(cleaned);
                    setValue(parsed === ''
                        ? ''
                        : Number(parsed).toLocaleString('en-IN', {
                            minimumFractionDigits: decimals,
                            maximumFractionDigits: decimals
                        }));
                    onClose(true);
                }, onKeyDown: (e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        commitValue(value);
                        e.stopPropagation();
                        onClose(true);
                    }
                    else if (e.key === 'Escape') {
                        e.preventDefault();
                        e.stopPropagation();
                        onClose(true);
                    }
                    else if (e.key === 'Tab') {
                        e.stopPropagation();
                        commitValue(value);
                        onClose(true);
                    }
                    else {
                        e.stopPropagation();
                    }
                }, className: "p-inputtext p-inputtext-sm", style: { width: '100%' } })));
    };
    const handleColumnEditor = (editor, row, onRowChange, onClose, colKey, options) => {
        switch (editor) {
            case 'dropdown':
                return React.createElement(DropdownEditor, { row: row, colKey: colKey, options: options, onRowChange: onRowChange, onClose: onClose });
            case 'number':
                return React.createElement(NumberEditor, { row: row, colKey: colKey, onRowChange: onRowChange, onClose: onClose });
            case 'calendar':
            case 'date':
                return React.createElement(CalendarEditor, { row: row, colKey: colKey, onRowChange: onRowChange, onClose: onClose, mode: "date" });
            case 'time':
                return React.createElement(CalendarEditor, { row: row, colKey: colKey, onRowChange: onRowChange, onClose: onClose, mode: "time" });
            case 'disabled':
                return null;
            case 'text':
            default:
                return React.createElement(TextEditor, { row: row, colKey: colKey, onRowChange: onRowChange, onClose: onClose });
        }
    };
    const resolveDropdownOptions = (col, row) => {
        if (!(col === null || col === void 0 ? void 0 : col.options))
            return [];
        if (typeof col.options === 'function') {
            return col.options(row) || [];
        }
        return col.options;
    };
    const gridColumns = [
        ...(enableRowSelection ? [SelectColumn] : []),
        ...(showRowDelete && allowDeleteRow && deleteColumnPosition === 'start'
            ? [
                {
                    key: '__delete__',
                    name: '',
                    width: 60,
                    resizable: false,
                    sortable: false,
                    formatter: ({ row }) => {
                        if (!row)
                            return null;
                        if (deleteButtonVariant === 'prime') {
                            return (React.createElement(Button, { style: { height: '35px', width: '20px', marginLeft: 20 }, type: "button", onClick: () => handleDeleteRow(row), className: "p-button-rounded p-button-text", icon: "pi pi-trash" }));
                        }
                        return (React.createElement("i", { className: "pi pi-trash", "aria-hidden": "true", onClick: () => handleDeleteRow(row), title: "Delete", style: { cursor: 'pointer' } }));
                    }
                }
            ]
            : []),
        ...columns.map((col) => (Object.assign(Object.assign({}, col), { name: `${col.name}${col.required ? ' *' : ''}`, editor: (col === null || col === void 0 ? void 0 : col.editor) === 'disabled'
                ? undefined
                : (props) => handleColumnEditor(col === null || col === void 0 ? void 0 : col.editor, props.row, props.onRowChange, props.onClose, col.key, resolveDropdownOptions(col, props.row)), formatter: ({ row }) => {
                if (typeof col.formatter === 'function') {
                    return col.formatter({ row });
                }
                if (col.key === 'qty')
                    return formatIndianForDisplay(row[col.key], 3);
                if (col.key === 'netamt')
                    return formatIndianForDisplay(row[col.key], 2);
                if (col.key === 'gst')
                    return formatIndianForDisplay(row[col.key], 2);
                if (col.key === 'gstamt')
                    return formatIndianForDisplay(row[col.key], 2);
                if (col.editor === 'dropdown' && col.options) {
                    const options = resolveDropdownOptions(col, row);
                    const selected = options.find((opt) => opt.value === row[col.key]);
                    return selected ? selected.label : '';
                }
                return row[col.key];
            } }))),
        ...(showRowDelete && allowDeleteRow && deleteColumnPosition === 'end'
            ? [
                {
                    key: '__delete__',
                    name: '',
                    width: 60,
                    resizable: false,
                    sortable: false,
                    formatter: ({ row }) => {
                        if (!row)
                            return null;
                        if (deleteButtonVariant === 'prime') {
                            return (React.createElement(Button, { style: { height: '35px', width: '20px', marginLeft: 20 }, type: "button", onClick: () => handleDeleteRow(row), className: "p-button-rounded p-button-text", icon: "pi pi-trash" }));
                        }
                        return (React.createElement("i", { className: "pi pi-trash", "aria-hidden": "true", onClick: () => handleDeleteRow(row), title: "Delete", style: { cursor: 'pointer' } }));
                    }
                }
            ]
            : [])
    ];
    const gridColumnsWithStretch = React.useMemo(() => {
        if (!stretchLastColumn)
            return gridColumns;
        if (!containerWidth || containerWidth <= 0)
            return gridColumns;
        if (!columns.length)
            return gridColumns;
        // Stretch the last "user column" (not the internal delete column)
        const lastUserCol = columns[columns.length - 1];
        if (!(lastUserCol === null || lastUserCol === void 0 ? void 0 : lastUserCol.key))
            return gridColumns;
        const leadingWidth = (enableRowSelection ? 35 : 0) + (showRowDelete && allowDeleteRow && deleteColumnPosition === 'start' ? 60 : 0);
        const trailingWidth = showRowDelete && allowDeleteRow && deleteColumnPosition === 'end' ? 60 : 0;
        const defaultWidth = 120;
        const otherUserColsWidth = columns
            .slice(0, -1)
            .reduce((sum, c) => sum + (Number(c.width) || defaultWidth), 0);
        const lastWidth = Number(lastUserCol.width) || defaultWidth;
        const available = containerWidth - leadingWidth - trailingWidth - otherUserColsWidth;
        const nextLastWidth = Math.max(lastWidth, available);
        return gridColumns.map((c) => {
            if ((c === null || c === void 0 ? void 0 : c.key) === lastUserCol.key) {
                return Object.assign(Object.assign({}, c), { width: nextLastWidth });
            }
            return c;
        });
    }, [
        allowDeleteRow,
        columns,
        deleteColumnPosition,
        enableRowSelection,
        gridColumns,
        showRowDelete,
        stretchLastColumn
    ]);
    const getColWidth = (col) => {
        const width = Number(col === null || col === void 0 ? void 0 : col.width);
        if (Number.isFinite(width) && width > 0)
            return width;
        const minWidth = Number(col === null || col === void 0 ? void 0 : col.minWidth);
        if (Number.isFinite(minWidth) && minWidth > 0)
            return minWidth;
        return 120;
    };
    const gridPixelWidth = React.useMemo(() => {
        if (!(gridColumnsWithStretch === null || gridColumnsWithStretch === void 0 ? void 0 : gridColumnsWithStretch.length))
            return 0;
        return gridColumnsWithStretch.reduce((sum, c) => sum + getColWidth(c), 0);
    }, [gridColumnsWithStretch]);
    const getNextEditableColIndex = (startIndex, direction) => {
        var _a, _b;
        let nextIndex = startIndex + direction;
        while (nextIndex >= 0 && nextIndex < columns.length) {
            if (((_a = columns[nextIndex]) === null || _a === void 0 ? void 0 : _a.editor) && ((_b = columns[nextIndex]) === null || _b === void 0 ? void 0 : _b.editor) !== 'disabled')
                return nextIndex;
            nextIndex += direction;
        }
        return null;
    };
    const focusCell = (rowElement, colIndex) => {
        const leadingCols = (enableRowSelection ? 1 : 0) + (showRowDelete && allowDeleteRow && deleteColumnPosition === 'start' ? 1 : 0);
        const gridColIndex = colIndex + leadingCols;
        const cell = rowElement.querySelector(`[aria-colindex="${gridColIndex + 1}"]`);
        activateCell(cell, true);
    };
    const handleTabNavigation = (focusedCell, isShiftTab) => {
        const rowElement = focusedCell.closest('[role="row"]');
        if (!rowElement)
            return;
        const ariaColIndex = parseInt(focusedCell.getAttribute('aria-colindex') || '', 10);
        if (!ariaColIndex)
            return;
        const leadingCols = (enableRowSelection ? 1 : 0) + (showRowDelete && allowDeleteRow && deleteColumnPosition === 'start' ? 1 : 0);
        const gridIdx = ariaColIndex - 1;
        const currentColIndex = gridIdx - leadingCols;
        const direction = isShiftTab ? -1 : 1;
        const nextColIndex = getNextEditableColIndex(currentColIndex, direction);
        if (nextColIndex !== null) {
            focusCell(rowElement, nextColIndex);
            return;
        }
        const grid = focusedCell.closest('[role="grid"]');
        if (!grid)
            return;
        const bodyRows = getBodyRows(grid);
        const currentBodyRowIndex = bodyRows.indexOf(rowElement);
        if (currentBodyRowIndex < 0)
            return;
        const nextRowIndex = currentBodyRowIndex + direction;
        // If an inline add-row is rendered, don't focus it; add a real row instead.
        if (!isShiftTab && showInlineAddRow && nextRowIndex >= rows.length) {
            handleAddRow();
            return;
        }
        if (nextRowIndex >= 0 && nextRowIndex < bodyRows.length) {
            const nextRow = bodyRows[nextRowIndex];
            const firstEditableIndex = direction === 1 ? 0 : columns.length - 1;
            const nextEditableColIndex = getNextEditableColIndex(firstEditableIndex - direction, direction);
            if (nextEditableColIndex !== null)
                focusCell(nextRow, nextEditableColIndex);
            return;
        }
        if (!isShiftTab && nextRowIndex >= bodyRows.length) {
            handleAddRow();
        }
    };
    const handleKeyDown = (e) => {
        var _a, _b;
        const target = e.target;
        const focusedCell = (_a = target === null || target === void 0 ? void 0 : target.closest('[role="gridcell"]')) !== null && _a !== void 0 ? _a : (_b = document.activeElement) === null || _b === void 0 ? void 0 : _b.closest('[role="gridcell"]');
        if (!focusedCell)
            return;
        const rowElement = focusedCell.closest('[role="row"]');
        if (!rowElement)
            return;
        const ariaColIndex = parseInt(focusedCell.getAttribute('aria-colindex') || '', 10);
        if (!ariaColIndex)
            return;
        const leadingCols = (enableRowSelection ? 1 : 0) + (showRowDelete && allowDeleteRow && deleteColumnPosition === 'start' ? 1 : 0);
        const gridIdx = ariaColIndex - 1;
        const colIndex = gridIdx - leadingCols;
        const column = columns[colIndex];
        const isInEditor = !!(target === null || target === void 0 ? void 0 : target.closest('.rdg-editor-container'));
        if (e.key === 'Enter') {
            if (!column)
                return;
            if (target === null || target === void 0 ? void 0 : target.closest('.p-dropdown-panel'))
                return;
            if ((column === null || column === void 0 ? void 0 : column.editor) === 'dropdown' && !isInEditor) {
                e.preventDefault();
                activateCell(focusedCell, true);
                setTimeout(() => {
                    var _a;
                    const trigger = focusedCell.querySelector('.p-dropdown-trigger');
                    const root = focusedCell.querySelector('.p-dropdown');
                    (_a = (trigger !== null && trigger !== void 0 ? trigger : root)) === null || _a === void 0 ? void 0 : _a.click();
                }, 0);
                return;
            }
            if (!isInEditor) {
                const allowEnter = column === null || column === void 0 ? void 0 : column.allowEnter;
                if (!allowEnter)
                    e.preventDefault();
            }
        }
        if (e.key === 'Tab') {
            e.preventDefault();
            Promise.resolve().then(() => {
                var _a, _b;
                const activeCell = (_b = (_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.closest('[role="gridcell"]')) !== null && _b !== void 0 ? _b : focusedCell;
                handleTabNavigation(activeCell, e.shiftKey);
            });
        }
    };
    const calculateColumnSum = (key) => {
        const sum = rows.reduce((sum, row) => {
            const value = parseFloat(row[key]);
            return sum + (isNaN(value) ? 0 : value);
        }, 0);
        return sum.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };
    return (React.createElement("div", { className: "space-y-3" },
        React.createElement("div", { className: "flex gap-2 mb-2" },
            showAddRowButton && !showInlineAddRow && (React.createElement(Button, { label: "Add Row", icon: "pi pi-plus", onClick: (e) => {
                    e.preventDefault();
                    handleAddRow();
                }, disabled: isAddDisabled(), className: "p-button-success", style: { width: 150 } })),
            showRemoveSelectedButton && enableRowSelection && (React.createElement(Button, { label: "Remove Selected", icon: "pi pi-trash", onClick: (e) => {
                    e.preventDefault();
                    handleBulkDelete();
                }, className: "p-button-danger", disabled: selectedRows.size === 0 || !allowDeleteRow, style: { width: 200 } }))),
        React.createElement("div", { ref: gridWrapRef, onKeyDownCapture: handleKeyDown, style: { width: '100%', overflowX: 'auto' } },
            React.createElement(DataGrid, { ref: gridRef, defaultColumnOptions: { sortable: true, resizable: true }, columns: gridColumnsWithStretch, rows: showInlineAddRow ? [...rows, inlineAddRowItem] : rows, onRowsChange: (updatedRows, data) => {
                    handleRowsChange(updatedRows, data);
                    onTableChange(true);
                }, renderers: {
                    rowRenderer: (key, props) => {
                        if (!showInlineAddRow || !isInlineAddRow(props.row)) {
                            return React.createElement(Row, Object.assign({ key: key }, props));
                        }
                        const { viewportColumns } = props, rest = __rest(props, ["viewportColumns"]);
                        const firstCol = viewportColumns === null || viewportColumns === void 0 ? void 0 : viewportColumns[0];
                        if (!firstCol)
                            return React.createElement(Row, Object.assign({ key: key }, props));
                        const newViewportColumns = [
                            Object.assign(Object.assign({}, firstCol), { minWidth: 160, width: 160, cellClass: 'action-cell-class', colSpan: () => viewportColumns.length, formatter: () => (React.createElement("div", { style: { paddingLeft: 8 } },
                                    React.createElement(Button, { icon: "pi pi-plus", type: "button", disabled: isAddDisabled(), label: inlineAddRowLabel, tabIndex: 1, style: { width: 80 }, onClick: (e) => {
                                            e.preventDefault();
                                            handleAddRow();
                                        }, className: "p-button-text" }))) })
                        ];
                        return React.createElement(Row, Object.assign({ key: key, viewportColumns: newViewportColumns }, rest));
                    }
                }, selectedRows: selectedRows, onSelectedRowsChange: setSelectedRows, rowKeyGetter: (row) => (isInlineAddRow(row) ? '__inline_add_row__' : row.id), className: className, style: (gridStyle !== null && gridStyle !== void 0 ? gridStyle : {
                    height: `${Math.max(1, rows.length + (showInlineAddRow ? 1 : 0)) * 35 + 35}px`,
                    '--rdg-header-background-color': '#827f7f',
                    width: stretchLastColumn ? '100%' : Math.max(1, gridPixelWidth),
                    overflowX: 'hidden',
                    overflowY: 'hidden'
                }) }),
            showFooter && (React.createElement("div", { className: className, style: {
                    display: 'grid',
                    height: '35px',
                    backgroundColor: '#e0e0e0',
                    fontWeight: 'bold',
                    alignItems: 'center',
                    paddingLeft: enableRowSelection ? '0px' : '5px'
                } },
                enableRowSelection && React.createElement("div", null),
                columns.map((col) => (React.createElement("div", { key: col.key, style: {
                        padding: '0 8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '100%'
                    } },
                    React.createElement("div", null, col.key === footerTotalColumnKey ? `Total Items : ${rows.length}` : ''),
                    React.createElement("div", null, col.key === footerTotalColumnKey ? `Total Taxable Amount : ₹ ${calculateColumnSum(footerTotalColumnKey)}` : '')))))))));
};
export default EditableTable;
